import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Send, CalendarRange, Loader2, X, CheckSquare, Square } from "lucide-react";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function daysAheadISO(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

const QUICK_RANGES = [
  { label: "Só hoje", start: () => todayISO(), end: () => todayISO() },
  { label: "Últimos 7 dias", start: () => daysAgoISO(7), end: () => todayISO() },
  { label: "Últimos 30 dias", start: () => daysAgoISO(30), end: () => todayISO() },
  { label: "Próximos 7 dias", start: () => todayISO(), end: () => daysAheadISO(7) },
];

export default function BulkBilling() {
  const [startDate, setStartDate] = useState(daysAgoISO(7));
  const [endDate, setEndDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [valorTotal, setValorTotal] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const search = async () => {
    if (!startDate) return toast.error("Informe a data inicial");
    setLoading(true);
    setItems(null);
    try {
      const { data } = await api.post("/billing/bulk/preview", {
        start_date: startDate,
        end_date: endDate || startDate,
      });
      setItems(data.items);
      setValorTotal(data.valor_total);
      setSelected(new Set(data.items.map((it) => it.title_id)));
      if (data.items.length === 0) toast.info("Nenhum boleto em aberto/vencido nesse intervalo");
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  const applyQuickRange = (qr) => {
    setStartDate(qr.start());
    setEndDate(qr.end());
  };

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (!items) return;
    setSelected((s) => (s.size === items.length ? new Set() : new Set(items.map((it) => it.title_id))));
  };

  const selectedValue = (items || [])
    .filter((it) => selected.has(it.title_id))
    .reduce((acc, it) => acc + (parseFloat(String(it.valor).replace(",", ".")) || 0), 0);

  const send = async () => {
    if (selected.size === 0) return toast.error("Selecione ao menos um boleto");
    setSending(true);
    try {
      const { data } = await api.post("/billing/bulk/send", {
        start_date: startDate,
        end_date: endDate || startDate,
        title_ids: Array.from(selected),
      });
      toast.success(`Envio iniciado — ${data.total} cobrança(s) na fila`);
      pollJob(data.job_id);
    } catch (ex) {
      toast.error(formatApiError(ex));
      setSending(false);
    }
  };

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/billing/bulk/jobs/${jobId}`);
        setJob(data);
        if (data.status !== "running") {
          clearInterval(pollRef.current);
          setSending(false);
          if (data.status === "done") {
            toast.success(`Lote concluído: ${data.sent} enviada(s), ${data.failed} falha(s)`);
          }
        }
      } catch {
        clearInterval(pollRef.current);
        setSending(false);
      }
    }, 2500);
  };

  const cancelJob = async () => {
    if (!job?._id) return;
    try {
      await api.post(`/billing/bulk/jobs/${job._id}/cancel`);
      toast.success("Cancelamento solicitado — para após o cliente atual");
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Cobrança</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Cobrança em Lote</h1>
        <p className="text-sm text-slate-500 mt-2">
          Selecione um período de vencimento e envie a cobrança completa (texto + QR + PIX copia-e-cola + PDF) pra
          todos os boletos em aberto ou vencidos nesse intervalo, de uma vez.
        </p>
      </header>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Vencimento de</Label>
            <Input
              data-testid="bulk-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 bg-slate-950 border-slate-800 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">até</Label>
            <Input
              data-testid="bulk-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 bg-slate-950 border-slate-800 text-white"
            />
          </div>
          <Button data-testid="bulk-search-btn" onClick={search} disabled={loading} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            Buscar
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {QUICK_RANGES.map((qr) => (
            <button
              key={qr.label}
              onClick={() => applyQuickRange(qr)}
              className="text-xs px-3 py-1.5 rounded-full border border-slate-700 text-slate-400 hover:border-emerald-500/50 hover:text-emerald-300 transition-colors"
            >
              <CalendarRange className="w-3 h-3 inline mr-1 -mt-0.5" /> {qr.label}
            </button>
          ))}
        </div>
      </Card>

      {items && (
        <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={toggleAll} className="text-slate-400 hover:text-emerald-300">
                {items.length > 0 && selected.size === items.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              </button>
              <span className="text-sm text-slate-400">
                {items.length} boleto(s) encontrado(s) · <span className="text-emerald-300 font-mono-jb">R$ {valorTotal.toFixed(2).replace(".", ",")}</span> no total
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-400">
                {selected.size} selecionado(s) · <span className="text-emerald-300 font-mono-jb">R$ {selectedValue.toFixed(2).replace(".", ",")}</span>
              </span>
              <Button
                data-testid="bulk-send-btn"
                onClick={send}
                disabled={sending || selected.size === 0}
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Enviar selecionadas
              </Button>
            </div>
          </div>

          {job && (
            <div className="px-6 py-4 border-b border-slate-800 bg-slate-950/50" data-testid="bulk-job-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white font-medium">
                  {job.status === "running" ? "Enviando…" : job.status === "done" ? "Concluído" : "Cancelado"}{" "}
                  ({job.sent + job.failed} / {job.total})
                </span>
                {job.status === "running" && (
                  <button onClick={cancelJob} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                    <X className="w-3 h-3" /> Cancelar
                  </button>
                )}
              </div>
              <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${job.total ? ((job.sent + job.failed) / job.total) * 100 : 0}%` }}
                />
              </div>
              <div className="text-xs text-slate-500 mt-2">
                <span className="text-emerald-400">{job.sent} enviada(s)</span>
                {job.failed > 0 && <span className="text-red-400 ml-3">{job.failed} falha(s)</span>}
              </div>
              {job.errors?.length > 0 && (
                <div className="mt-2 text-[11px] text-slate-500 max-h-24 overflow-y-auto space-y-0.5">
                  {job.errors.slice(-8).map((e, i) => (
                    <div key={i}>⚠️ {e}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left py-3 px-4 w-10"></th>
                  <th className="text-left py-3 px-4">Cliente</th>
                  <th className="text-left py-3 px-4">Telefone</th>
                  <th className="text-left py-3 px-4">Vencimento</th>
                  <th className="text-left py-3 px-4">Situação</th>
                  <th className="text-left py-3 px-4">Valor</th>
                </tr>
              </thead>
              <tbody data-testid="bulk-items-tbody">
                {items.map((it) => (
                  <tr key={it.title_id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                    <td className="py-2.5 px-4">
                      <button onClick={() => toggle(it.title_id)} className="text-slate-400 hover:text-emerald-300">
                        {selected.has(it.title_id) ? <CheckSquare className="w-4 h-4 text-emerald-400" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="py-2.5 px-4 text-white">{it.nome}</td>
                    <td className="py-2.5 px-4 font-mono-jb text-slate-400 text-xs">{it.celular || "—"}</td>
                    <td className="py-2.5 px-4 font-mono-jb text-slate-400 text-xs">
                      {it.datavenc ? new Date(it.datavenc + "T00:00:00").toLocaleDateString("pt-BR") : "—"}
                    </td>
                    <td className="py-2.5 px-4">
                      {it.dias_atraso == null ? (
                        "—"
                      ) : it.dias_atraso > 0 ? (
                        <Badge className="bg-red-500/15 text-red-300 border border-red-500/30">{it.dias_atraso}d atraso</Badge>
                      ) : it.dias_atraso === 0 ? (
                        <Badge className="bg-orange-500/15 text-orange-300 border border-orange-500/30">Vence hoje</Badge>
                      ) : (
                        <Badge className="bg-sky-500/15 text-sky-300 border border-sky-500/30">Vence em {-it.dias_atraso}d</Badge>
                      )}
                    </td>
                    <td className="py-2.5 px-4 font-mono-jb text-emerald-300">
                      R$ {parseFloat(String(it.valor).replace(",", ".")).toFixed(2).replace(".", ",")}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-500">
                      Nenhum boleto em aberto ou vencido nesse período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
